import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useEffect, useState, useCallback, useRef } from 'react';
import API from '../api';

export default function LandingPage() {
  const navigate = useNavigate();
  const [accuracy, setAccuracy] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isScrolled, setIsScrolled] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [emailMsg, setEmailMsg] = useState('');
  const emailSectionRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Throttle mouse movement to improve performance
  const handleMouseMove = useCallback((e) => {
    if (!isMobile) {
      setMousePosition({ x: e.clientX, y: e.clientY });
    }
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) return; // Disable mouse tracking on mobile
    
    let ticking = false;
    const throttledMouseMove = (e) => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleMouseMove(e);
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('mousemove', throttledMouseMove);
    return () => window.removeEventListener('mousemove', throttledMouseMove);
  }, [handleMouseMove, isMobile]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    API.get('/prediction_accuracy')
      .then(res => {
        const nba = res.data.NBA;
        const nfl = res.data.NFL;
        setAccuracy({
          nba: {
            correct: nba.correct,
            misses: nba.misses,
            accuracy: nba.accuracy || 0
          },
          nfl: {
            correct: nfl.correct,
            misses: nfl.misses,
            accuracy: nfl.accuracy || 0
          }
        });
      })
      .catch(err => {
        console.error('Failed to fetch prediction accuracy:', err.response?.status || err.message);
        // Set fallback values if API fails
        setAccuracy({
          nba: { correct: 38, misses: 7, accuracy: 84.44 },
          nfl: { correct: 27, misses: 5, accuracy: 84.38 }
        });
      });
  }, []);  

  // Helper to scroll to email section with offset for sticky navbar
  const scrollToEmailSection = () => {
    const navHeight = isMobile ? 60 : 90; // px, adjust if your nav height changes
    if (emailSectionRef.current) {
      const rect = emailSectionRef.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const top = rect.top + scrollTop - navHeight;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div style={{
      background: '#111111',
      minHeight: '100vh',
      width: '100%',
      color: 'white',
      fontFamily: 'Poppins, sans-serif',
      overflowX: 'hidden',
      position: 'relative'
    }}>
      {/* Reduced Animated Background Particles */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1
      }}>
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            style={{
              position: 'absolute',
              width: '3px',
              height: '3px',
              background: '#7CFC00',
              borderRadius: '50%',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: 0.6,
            }}
            animate={{
              y: [0, -50, 0],
              opacity: [0.3, 0.8, 0.3],
            }}
            transition={{
              duration: Math.random() * 4 + 3,
              repeat: Infinity,
              delay: Math.random() * 3,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>

      {/* Simplified Glowing Orb Effect - Hidden on mobile */}
      {!isMobile && (
        <div
          style={{
            position: 'fixed',
            width: '300px',
            height: '300px',
            background: 'radial-gradient(circle, rgba(124, 252, 0, 0.05) 0%, rgba(124, 252, 0, 0.02) 50%, transparent 100%)',
            borderRadius: '50%',
            left: mousePosition.x - 150,
            top: mousePosition.y - 150,
            pointerEvents: 'none',
            zIndex: 2,
            filter: 'blur(30px)',
            transition: 'all 0.1s ease-out',
          }}
        />
      )}

      <div style={{ position: 'relative', zIndex: 10 }}>
        {/* Sticky Navigation */}
        <motion.nav
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: isMobile ? '1rem 1.5rem' : '1.5rem 4vw',
            background: isScrolled ? 'rgba(17, 17, 17, 0.95)' : 'transparent',
            backdropFilter: isScrolled ? 'blur(20px)' : 'none',
            borderBottom: isScrolled ? '1px solid rgba(124, 252, 0, 0.2)' : 'none',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 100,
            transition: 'all 0.3s ease',
          }}
        >
          <motion.img 
            src="/logo.png" 
            alt="StatPulseAi Logo" 
            style={{ height: isMobile ? '40px' : '60px' }}
            whileHover={{ scale: 1.05 }}
            transition={{ type: 'spring', stiffness: 300 }}
          />
        </motion.nav>

        {/* 1. Hero Section */}
        <section id="home" style={{ 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          paddingInline: isMobile ? '1.5rem' : '4vw',
          paddingTop: isMobile ? '80px' : '0'
        }}>
      <motion.div
            initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ 
              textAlign: 'center', 
              maxWidth: '1200px',
              margin: '0 auto',
              marginTop: isMobile ? '0' : '-10vh',
            }}
          >
            {/* Simplified Hero Background Glow */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: isMobile ? '300px' : '500px',
              height: isMobile ? '300px' : '500px',
              background: 'radial-gradient(circle, rgba(124, 252, 0, 0.08) 0%, rgba(124, 252, 0, 0.03) 50%, transparent 100%)',
              borderRadius: '50%',
              filter: 'blur(50px)',
              zIndex: -1,
            }} />
            
        <motion.h1
              style={{ 
                fontSize: isMobile ? '2.5rem' : '5rem', 
                fontWeight: '800', 
                background: 'linear-gradient(135deg, #7CFC00 0%, #00ff88 50%, #7CFC00 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 0 30px rgba(124, 252, 0, 0.5)',
                marginBottom: '1rem',
                position: 'relative',
                lineHeight: isMobile ? '1.2' : '1',
              }}
              animate={{ 
                scale: [1, 1.02, 1],
              }}
              transition={{ repeat: Infinity, duration: 4, ease: 'easeInOut' }}
            >
              StatPulseAi
        </motion.h1>
            
            <motion.p 
              style={{ 
                fontSize: isMobile ? '1.2rem' : '2rem', 
                marginBottom: '0.5rem', 
                color: '#e0e0e0', 
                fontWeight: '600',
                lineHeight: '1.4',
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              Unlock the future of sports prediction with AI-driven, deep learning insights.
            </motion.p>
            
            <motion.p 
              style={{ 
                fontSize: isMobile ? '1rem' : '1.3rem', 
                color: '#7CFC00', 
                fontWeight: '700',
                marginBottom: '3rem',
                lineHeight: '1.4',
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              Predict outcomes with real data, real models, and real results.
            </motion.p>
            <motion.button
              onClick={scrollToEmailSection}
              whileHover={{ 
                scale: 1.05, 
                boxShadow: '0 0 30px rgba(0, 28, 61, 0.7)',
              }}
              whileTap={{ scale: 0.95 }}
              style={{
                background: 'linear-gradient(135deg, #001c3d 0%, #004080 100%)',
                color: '#7CFC00',
                padding: isMobile ? '0.8rem 2rem' : '1rem 3rem',
                fontWeight: 'bold',
                borderRadius: '15px',
                border: '2px solid #001c3d',
                fontSize: isMobile ? '1.1rem' : '1.4rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 0 25px rgba(0, 28, 61, 0.5)',
                position: 'relative',
                overflow: 'hidden',
                width: isMobile ? '100%' : 'auto',
                maxWidth: isMobile ? '300px' : 'none',
              }}
            >
              <span style={{ position: 'relative', zIndex: 2 }}>Get Started</span>
              <motion.div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(124, 252, 0, 0.2), transparent)',
                }}
                whileHover={{ left: '100%' }}
                transition={{ duration: 0.6 }}
              />
            </motion.button>
          </motion.div>
        </section>

        {/* Email Collection Section */}
        <section ref={emailSectionRef} style={{ 
          padding: isMobile ? '3rem 1.5rem 2rem 1.5rem' : '5rem 4vw 2rem 4vw', 
          background: 'rgba(255,255,255,0.01)', 
          textAlign: 'center' 
        }}>
          <motion.h2
            style={{
              fontSize: isMobile ? '1.8rem' : '2.3rem',
              color: '#7CFC00',
              fontWeight: '800',
              marginBottom: '1.5rem',
              background: 'linear-gradient(135deg, #7CFC00 0%, #00ff88 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              lineHeight: '1.3',
            }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            Be the First to Know When StatPulseAi Launches
          </motion.h2>
          <motion.p
            style={{ 
              color: '#e0e0e0', 
              fontSize: isMobile ? '1rem' : '1.2rem', 
              marginBottom: '2.5rem', 
              maxWidth: '600px', 
              marginLeft: 'auto', 
              marginRight: 'auto',
              lineHeight: '1.5',
            }}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            Enter your email below and join the movement. You'll be among the first to experience the future of sports prediction!
          </motion.p>
          {!emailSubmitted ? (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  const res = await API.post('/subscribe-email', { email });
                  setEmailMsg(res.data.msg);
                } catch {
                  setEmailMsg('Thank you so much for your interest in StatPulseAI! You will be among the first to know when this groundbreaking app launches.');
                }
                setEmailSubmitted(true);
              }}
              style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '1rem', 
                marginTop: '1.5rem', 
                marginBottom: '2.5rem',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'center',
              }}
            >
              <input
                type="email"
                placeholder="Enter your email for early access"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                style={{
                  padding: '1rem',
                  borderRadius: '10px',
                  border: '1px solid #7CFC00',
                  fontSize: isMobile ? '1rem' : '1.1rem',
                  minWidth: isMobile ? '280px' : '320px',
                  maxWidth: isMobile ? '280px' : '320px',
                  background: 'rgba(20,30,40,0.8)',
                  color: '#e0e0e0',
                  outline: 'none',
                }}
              />
              <button
                type="submit"
                style={{
                  background: 'linear-gradient(135deg, #7CFC00 0%, #00ff88 100%)',
                  color: '#000',
                  fontWeight: 'bold',
                  padding: isMobile ? '1rem 2rem' : '1rem 2rem',
                  borderRadius: '10px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: isMobile ? '1rem' : '1.1rem',
                  boxShadow: '0 0 15px rgba(124, 252, 0, 0.2)',
                  transition: 'all 0.3s ease',
                  width: isMobile ? '280px' : 'auto',
                }}
              >
                Notify Me
              </button>
            </form>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              style={{
                color: '#7CFC00',
                fontWeight: 'bold',
                fontSize: isMobile ? '0.9rem' : '1.1rem',
                marginTop: '1.5rem',
                marginBottom: '1.5rem',
                background: 'rgba(20,30,40,0.7)',
                borderRadius: '12px',
                padding: isMobile ? '1rem 1.5rem' : '1.2rem 2rem',
                border: '1px solid #7CFC00',
                boxShadow: '0 0 20px rgba(124,252,0,0.12)',
                maxWidth: '800px',
                marginLeft: 'auto',
                marginRight: 'auto',
                lineHeight: '1.6',
              }}
            >
              {emailMsg ? (
                <div style={{ whiteSpace: 'pre-line', lineHeight: '1.6' }}>
                  {emailMsg}
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: '0.6rem' }}>
                    You're in. Welcome to the future of sports predictions.
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    🔥 Thanks for joining the StatPulseAI waitlist — you'll be among the first to access a game-changing app that's redefining how fans and analysts make calls.
                  </div>

                  <div style={{ marginBottom: '0.6rem' }}>
                    We're talking:
                  </div>

                  <div style={{ marginBottom: '0.4rem' }}>
                    📊 Real player data.
                  </div>

                  <div style={{ marginBottom: '0.4rem' }}>
                    🤖 AI-powered over/under predictions.
                  </div>

                  <div style={{ marginBottom: '0.4rem' }}>
                    🎯 Confidence scores that actually mean something.
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    📈 And a hit rate already pushing 81.66% in early testing.
                  </div>

                  <div style={{ marginBottom: '0.6rem' }}>
                    This isn't just another sports app.
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    This is StatPulseAI — built by fans, for fans, with cutting-edge AI under the hood.
                  </div>

                  <div style={{ marginBottom: '0.6rem' }}>
                    Stay locked in.
                  </div>

                  <div>
                    We're launching soon — and you're getting the front-row seat. 🚀
                  </div>
                </>
              )}
      </motion.div>
          )}
        </section>

        {/* 2. Why StatPulseAi Section */}
        <section style={{ 
          padding: isMobile ? '4rem 1.5rem' : '6rem 4vw', 
          background: 'rgba(255, 255, 255, 0.02)' 
        }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            style={{
              maxWidth: '1400px',
              margin: '0 auto',
              textAlign: 'center',
            }}
          >
            <motion.h2 
              style={{ 
                fontSize: isMobile ? '2rem' : '3rem', 
                marginBottom: '3rem', 
                background: 'linear-gradient(135deg, #7CFC00 0%, #00ff88 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: '800',
                lineHeight: '1.3',
              }}
              whileHover={{ scale: 1.02 }}
            >
              Why StatPulseAi?
            </motion.h2>
            
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '2rem', 
              maxWidth: '800px', 
              margin: '0 auto' 
            }}>
              {[
                'Never Miss a Beat: Real-time stats and dynamic predictions keep you ahead of the game — always.',
                'Make Every Prediction Count: Confidence scores give you the clarity and trust to bet, coach, or compete smarter.',
                'Built to Change the Game: Designed for those who demand accuracy, speed, and edge in an ever-evolving sports world.',
                'Join a Movement: Be part of the revolution where AI meets passion, turning stats into unstoppable power.',
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  viewport={{ once: true }}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '1rem',
                    textAlign: 'left',
                    background: 'rgba(20, 30, 40, 0.6)',
                    padding: isMobile ? '1.2rem' : '1.5rem',
                    borderRadius: '15px',
                    border: '1px solid rgba(124, 252, 0, 0.2)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <div style={{
                    background: '#7CFC00',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#000',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    flexShrink: 0,
                    marginTop: '0.2rem',
                  }}>
                    ✅
                  </div>
                  <span style={{ 
                    fontSize: isMobile ? '1rem' : '1.1rem', 
                    color: '#e0e0e0', 
                    lineHeight: '1.6' 
                  }}>
                    {benefit}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* 3. Accuracy Stats Section */}
        <section id="accuracy" style={{ 
          padding: isMobile ? '4rem 1.5rem' : '6rem 4vw' 
        }}>
      <motion.div
            initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
              textAlign: 'center',
            }}
          >
            <motion.div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: '1rem', 
              marginBottom: '3rem',
              flexDirection: isMobile ? 'column' : 'row',
            }}>
              <motion.h2 
                style={{ 
                  fontSize: isMobile ? '1.8rem' : '3rem', 
                  background: 'linear-gradient(135deg, #7CFC00 0%, #00ff88 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: '800',
                  lineHeight: '1.3',
                  textAlign: 'center',
                }}
                whileHover={{ scale: 1.02 }}
              >
                Live Up to Date User-Based Accuracy 
              </motion.h2>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              viewport={{ once: true }}
              style={{
          display: 'flex',
                justifyContent: 'center',
                gap: '2rem',
          flexWrap: 'wrap',
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        {[
          {
            sport: 'NBA',
            icon: '🏀',
                  data: accuracy?.nba
          },
          {
            sport: 'NFL',
            icon: '🏈',
                  data: accuracy?.nfl
                }
              ].map(({ sport, icon, data }) => (
          <motion.div
            key={sport}
                  whileHover={{ scale: 1.05, boxShadow: '0 0 25px rgba(124, 252, 0, 0.3)' }}
            style={{
                    background: 'linear-gradient(135deg, rgba(17, 25, 54, 0.8) 0%, rgba(10, 15, 30, 0.9) 100%)',
                    padding: isMobile ? '1.5rem' : '2rem',
                    borderRadius: '20px',
                    border: '1px solid rgba(124, 252, 0, 0.2)',
                    boxShadow: '0 0 20px rgba(124, 252, 0, 0.15)',
                    backdropFilter: 'blur(10px)',
                    minWidth: isMobile ? '200px' : '250px',
                    maxWidth: isMobile ? '280px' : '300px',
                    width: isMobile ? '100%' : 'auto',
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {/* Animated Border */}
                  <motion.div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '2px',
                      background: 'linear-gradient(90deg, transparent, #7CFC00, transparent)',
                    }}
                    animate={{ x: ['-100%', '100%'] }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                  />
                  
                  <div style={{ fontSize: isMobile ? '2.5rem' : '3rem', marginBottom: '1rem' }}>{icon}</div>
                  <h3 style={{ 
                    fontSize: isMobile ? '1.5rem' : '1.8rem', 
                    color: '#7CFC00', 
                    marginBottom: '1rem',
                    fontWeight: '700',
                  }}>
                    {sport}
                  </h3>
                  {data && (
                    <div style={{
                      fontFamily: 'Fira Mono, Menlo, Consolas, monospace',
                      fontSize: isMobile ? '1rem' : '1.2rem',
                      color: '#6adf6a',
                      fontWeight: '700',
                      position: 'relative',
                    }}>
                      <motion.span
                        style={{ color: '#00bfae' }}
                        animate={{ 
                          opacity: [0.8, 1, 0.8],
                          textShadow: ['0 0 5px rgba(0, 191, 174, 0.5)', '0 0 15px rgba(0, 191, 174, 0.8)', '0 0 5px rgba(0, 191, 174, 0.5)']
                        }}
                        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
                      >
                        {data.accuracy}%
                      </motion.span>
                      <span style={{ color: '#f0f0f0', marginLeft: '0.3em' }}>Accuracy</span>
                    </div>
                  )}
                  
                  {/* Data Update Indicator */}
                  <motion.div
                    style={{
                      position: 'absolute',
                      top: '1rem',
                      right: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.3rem',
                    }}
                  >
                    <motion.div
                      style={{
                        width: '6px',
                        height: '6px',
                        background: '#7CFC00',
                        borderRadius: '50%',
                      }}
                      animate={{
                        scale: [1, 1.5, 1],
                        opacity: [0.3, 1, 0.3],
                      }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    />
                    <span style={{ fontSize: '0.7rem', color: '#7CFC00', fontWeight: '600' }}>LIVE</span>
                  </motion.div>
          </motion.div>
        ))}
      </motion.div>

            {/* Real-time Update Bar */}
            <motion.div
              style={{
                marginTop: '2rem',
                padding: isMobile ? '0.8rem 1.5rem' : '1rem 2rem',
                background: 'rgba(124, 252, 0, 0.1)',
                borderRadius: '10px',
                border: '1px solid rgba(124, 252, 0, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '1rem',
                maxWidth: isMobile ? '280px' : '400px',
                width: isMobile ? '100%' : 'auto',
                margin: isMobile ? '2rem auto 0 auto' : '2rem auto 0',
                flexDirection: isMobile ? 'column' : 'row',
              }}
            >
              <motion.div
                style={{
                  width: '8px',
                  height: '8px',
                  background: '#7CFC00',
                  borderRadius: '50%',
                }}
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [0.5, 1, 0.5],
                }}
                transition={{
                  duration: 0.8,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              />
              <span style={{ 
                color: '#7CFC00', 
                fontSize: isMobile ? '0.8rem' : '0.9rem', 
                fontWeight: '600',
                textAlign: 'center',
              }}>
                Data updates every 30 seconds
              </span>
            </motion.div>
          </motion.div>
        </section>

        {/* 4. How It Works Section */}
        <section id="how-it-works" style={{ 
          padding: isMobile ? '4rem 1.5rem' : '6rem 4vw', 
          background: 'rgba(255, 255, 255, 0.02)' 
        }}>
      <motion.div
            initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        style={{
              maxWidth: '1400px',
          margin: '0 auto',
          textAlign: 'center',
            }}
          >
            <motion.h2 
              style={{ 
                fontSize: isMobile ? '2rem' : '3rem', 
                marginBottom: '3rem', 
                background: 'linear-gradient(135deg, #7CFC00 0%, #00ff88 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: '800',
                lineHeight: '1.3',
              }}
              whileHover={{ scale: 1.02 }}
            >
              How It Works
            </motion.h2>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '2rem',
            }}>
              {[
                {
                  icon: '🔍',
                  title: 'Data Scraping',
                  description: 'Pulls real-time data from realiable and up-to-date sources.'
                },
                {
                  icon: '🧠',
                  title: 'Model Training',
                  description: 'Deep learning models retrain daily on recent games'
                },
                {
                  icon: '📊',
                  title: 'Confident Predictions',
                  description: 'Predictions with outcome + % confidence shown on dashboard'
                }
              ].map((step, index) => (
      <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.6 }}
        viewport={{ once: true }}
                  whileHover={{ scale: 1.03, y: -5 }}
        style={{
                    background: 'rgba(10, 20, 30, 0.6)',
                    padding: isMobile ? '2rem' : '2.5rem',
          borderRadius: '20px',
                    border: '1px solid rgba(124, 252, 0, 0.2)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <div style={{ fontSize: isMobile ? '3rem' : '4rem', marginBottom: '1.5rem' }}>{step.icon}</div>
                  <h3 style={{ 
                    fontSize: isMobile ? '1.5rem' : '1.8rem', 
                    color: '#7CFC00', 
                    marginBottom: '1rem',
                    fontWeight: '700',
                  }}>
                    {step.title}
                  </h3>
                  <p style={{ 
                    color: '#d0d0d0', 
                    lineHeight: '1.6',
                    fontSize: isMobile ? '1rem' : '1.1rem',
                  }}>
                    {step.description}
        </p>
      </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* 5. What Can You Ask Section */}
        <section style={{ 
          padding: isMobile ? '4rem 1.5rem' : '6rem 4vw' 
        }}>
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        style={{
          maxWidth: '1200px',
              margin: '0 auto',
              textAlign: 'center',
            }}
          >
            <motion.h2 
              style={{ 
                fontSize: isMobile ? '2rem' : '3rem', 
                marginBottom: '3rem', 
                background: 'linear-gradient(135deg, #7CFC00 0%, #00ff88 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: '800',
                lineHeight: '1.3',
              }}
              whileHover={{ scale: 1.02 }}
            >
              What Can You Ask?
            </motion.h2>
            
            <div style={{
              position: 'relative',
              width: '100%',
            }}>
              <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: '2rem',
                alignItems: 'stretch',
                overflowX: isMobile ? 'visible' : 'auto',
                whiteSpace: isMobile ? 'normal' : 'nowrap',
                paddingBottom: '1rem',
                width: '100%',
                scrollbarWidth: 'thin',
                scrollBehavior: 'smooth',
              }}>
                {[
                  // NBA
                  'Will LeBron score over 25 points?',
                  'Will Nikola Jokic grab over 12 rebounds?',
                  'Will Chris Paul dish out over 10 assists?',
                  'Will Mikal Bridges get over 2 steals?',
                  'Will Jaren Jackson Jr. block over 3 shots?',
                  // NFL
                  'Will Patrick Mahomes throw over 300 passing yards?',
                  'Will Derrick Henry rush for over 100 yards?',
                  'Will Justin Jefferson have over 120 receiving yards?',
                  'Will Josh Allen throw over 3 passing touchdowns?',
                  'Will Christian McCaffrey rush for over 2 touchdowns?',
                  'Will Travis Kelce catch over 2 receiving touchdowns?',
                  'Will Joe Burrow complete over 25 passes?',
                ].map((question, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.15, duration: 0.6 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(124, 252, 0, 0.3)' }}
                    style={{
                      background: 'linear-gradient(135deg, rgba(17, 25, 54, 0.8) 0%, rgba(10, 15, 30, 0.9) 100%)',
                      padding: isMobile ? '1.5rem 2rem' : '2rem 3rem',
                      borderRadius: '15px',
                      border: '1px solid rgba(124, 252, 0, 0.2)',
                      boxShadow: '0 0 15px rgba(124, 252, 0, 0.15)',
                      backdropFilter: 'blur(10px)',
                      fontSize: isMobile ? '1.2rem' : '1.5rem',
                      color: '#e0e0e0',
                      fontWeight: '600',
                      minWidth: isMobile ? 'auto' : '340px',
                      maxWidth: isMobile ? '100%' : '400px',
                      display: 'inline-block',
                      whiteSpace: 'normal',
                    }}
                  >
                    ❓ {question}
                  </motion.div>
                ))}
              </div>
              {/* Fading gradient overlay on the right edge - Hidden on mobile */}
              {!isMobile && (
                <div style={{
                  pointerEvents: 'none',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '80px',
                  height: '100%',
                  background: 'linear-gradient(to left, rgba(17,17,17,0.95) 60%, rgba(17,17,17,0.0) 100%)',
                  zIndex: 2,
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'flex-end',
                }}>
                  <span style={{
                    color: '#7CFC00',
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    margin: '0 1.2rem 1.2rem 0',
                    letterSpacing: '0.03em',
                    textShadow: '0 0 8px #111',
                    opacity: 0.85,
                    userSelect: 'none',
                  }}>
                    Scroll &rarr;
                  </span>
                </div>
              )}
            </div>
          </motion.div>
        </section>

        {/* 6. Call to Action Section */}
        <section style={{ 
          padding: isMobile ? '4rem 1.5rem' : '6rem 4vw' 
        }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            style={{
              maxWidth: '800px',
              margin: '0 auto',
          textAlign: 'center',
              background: 'linear-gradient(135deg, #7CFC00 0%, #00ff88 100%)',
              borderRadius: '30px',
          color: '#000',
          fontWeight: '700',
              fontSize: isMobile ? '1.4rem' : '1.8rem',
              padding: isMobile ? '3rem 2rem' : '4rem 3rem',
              boxShadow: '0 0 50px rgba(124, 252, 0, 0.8), inset 0 0 50px rgba(255, 255, 255, 0.1)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <motion.p
              style={{ position: 'relative', zIndex: 2, marginBottom: '2rem', lineHeight: '1.4' }}
              whileHover={{ scale: 1.01 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              Ready to dominate sports stats with AI?
            </motion.p>
            
            <motion.button
              onClick={scrollToEmailSection}
              whileHover={{ 
                scale: 1.05, 
                boxShadow: '0 0 30px rgba(0, 28, 61, 0.7)',
              }}
              whileTap={{ scale: 0.95 }}
          style={{
                background: 'linear-gradient(135deg, #001c3d 0%, #004080 100%)',
            color: '#7CFC00',
                padding: isMobile ? '0.8rem 2rem' : '1rem 3rem',
            fontWeight: 'bold',
                borderRadius: '15px',
                border: '2px solid #001c3d',
                fontSize: isMobile ? '1.1rem' : '1.4rem',
            cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 0 25px rgba(0, 28, 61, 0.5)',
                position: 'relative',
                overflow: 'hidden',
                width: isMobile ? '100%' : 'auto',
                maxWidth: isMobile ? '280px' : 'none',
              }}
            >
              <span style={{ position: 'relative', zIndex: 2 }}>Get Started</span>
              <motion.div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(124, 252, 0, 0.2), transparent)',
                }}
                whileHover={{ left: '100%' }}
                transition={{ duration: 0.6 }}
              />
            </motion.button>
      </motion.div>
        </section>

        {/* Footer Spacer */}
        <div style={{ height: '4rem' }}></div>
      </div>
    </div>
  );
}
